import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  ILogger,
  MediaStatus,
  MediaType,
  useGrippContext,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { useCallback } from 'react';
import { Linking, Pressable, StyleSheet, Text } from 'react-native';
import { Card, ProgressBar } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { XIcon } from '../crop-circle/icons';
import { colors } from '../crop-circle/tokens';
import { getMediaTitleForWidth } from './mediaUtils';
import { ProcessingPlaceholder } from './processingPlaceholder';
import { DocumentData, MediaComponent, MediaComponentProps } from './types';

// app + web, will need to separate the web and app components if the functionality diverges further
export const DocumentComponent: MediaComponent<DocumentData> = ({
  displayMode = 'card',
  onPress,
  canDelete,
  ...props
}: MediaComponentProps<DocumentData>) => {
  const { logger } = useGrippContext();
  const documentCardStyle =
    displayMode === 'card'
      ? styles.documentThumbCard
      : styles.documentOutlinedCard;
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const defaultWidth = isCropCircleRedesignEnabled ? 88 : 80;
  const defaultHeight = isCropCircleRedesignEnabled ? 88 : 80;
  const displayWidth = props.displayWidth ?? defaultWidth;
  const displayHeight =
    props.displayWidth && props.displayWidth > defaultHeight
      ? props.displayWidth
      : defaultHeight;
  const fileIconSize = displayWidth / 4;

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress();
      return;
    }
    downloadDocument(props.mediaItem.status, props.mediaItem.url, logger);
  }, [props.mediaItem.status, props.mediaItem.url, logger, onPress]);

  // TODO: Crop Circle Redesign | Remove conditional rendering when feature is fully launched
  if (!isCropCircleRedesignEnabled) {
    return (
      <ProcessingPlaceholder
        processing={props.mediaItem.showProcessing ?? false}
        width={displayWidth}
        height={displayHeight}
        displayMode={displayMode}
        url={props.mediaItem.url}
        name={props.mediaItem.name}
        extension={props.mediaItem.extension}
      >
        <Card
          onPress={handlePress}
          mode={displayMode === 'outlined' ? 'outlined' : 'elevated'}
          style={[
            styles.baseCard,
            { width: displayWidth, height: displayHeight },
          ]}
        >
          <Card.Content
            style={[
              styles.cardContent,
              { width: displayWidth, height: displayHeight },
            ]}
          >
            <MaterialCommunityIcons
              style={styles.icon}
              name={
                props.mediaItem.type === MediaType.Document ? 'file' : 'video'
              }
              color={Colors.primaryDark}
              size={fileIconSize}
            />
            <Text style={styles.documentTitle}>
              {getMediaTitleForWidth(
                props.mediaItem.name,
                props.mediaItem.extension,
                displayWidth
              )}
            </Text>
          </Card.Content>
          {props.mediaItem.isUploading && (
            <ProgressBar animatedValue={props.mediaItem.uploadProgress} />
          )}
        </Card>
      </ProcessingPlaceholder>
    );
  }

  return (
    <ProcessingPlaceholder
      processing={props.mediaItem.showProcessing ?? false}
      width={defaultWidth}
      height={defaultHeight}
      displayMode={displayMode}
      url={props.mediaItem.url}
      name={props.mediaItem.name}
      extension={props.mediaItem.extension}
    >
      <Card
        onPress={handlePress}
        mode={displayMode === 'outlined' ? 'outlined' : 'elevated'}
        style={[
          styles.baseCardCropCircle,
          { width: defaultWidth, height: defaultHeight },
        ]}
      >
        {canDelete && (
          <Pressable
            style={styles.closeButton}
            onPress={() => props.deleteMediaItem?.(props.mediaItem.id)}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={
              props.mediaItem.type === MediaType.Document
                ? 'Remove document'
                : 'Remove video'
            }
          >
            <XIcon size={8} color={colors.slate[800]} />
          </Pressable>
        )}
        <Card.Content
          style={[
            styles.cardContent,
            { width: defaultWidth, height: defaultHeight },
          ]}
        >
          <MaterialCommunityIcons
            style={styles.icon}
            name={
              props.mediaItem.type === MediaType.Document ? 'file' : 'video'
            }
            color={Colors.primaryDark}
            size={fileIconSize}
          />
          <Text style={styles.documentTitle}>
            {getMediaTitleForWidth(
              props.mediaItem.name,
              props.mediaItem.extension,
              defaultWidth
            )}
          </Text>
        </Card.Content>
        {props.mediaItem.isUploading && (
          <ProgressBar animatedValue={props.mediaItem.uploadProgress} />
        )}
      </Card>
    </ProcessingPlaceholder>
  );
};

const downloadDocument = async (
  mediaStatus: MediaStatus,
  url: string,
  logger?: ILogger
) => {
  if (isLocalUrl(url)) {
    await openUrl(url);
    return;
  }
  fetch(url, { method: 'GET' })
    .then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        await openUrl(data.downloadUrl);
      }
    })
    .catch((e) => {
      logger?.error(e, `Failed to download document from ${url}`);
    });
};

const openUrl = async (url: string) => {
  const supported = await Linking.canOpenURL(url);
  if (supported) {
    await Linking.openURL(url);
  }
};

const isLocalUrl = (url: string) => {
  return url.startsWith('blob:') || url.startsWith('file://');
};

const styles = StyleSheet.create({
  icon: {
    alignSelf: 'center',
  },
  baseCard: {
    borderRadius: 8,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Colors.secondaryGrayHeader,
    cursor: 'pointer',
  },
  baseCardCropCircle: {
    borderRadius: 4,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Colors.secondaryGrayHeader,
    cursor: 'pointer',
  },
  cardContent: {
    paddingTop: 0,
    paddingHorizontal: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  documentThumbCard: {},
  documentOutlinedCard: {
    borderWidth: 0.5,
    borderColor: Colors.grayDivider,
  },
  documentTitle: {
    textAlign: 'center',
    fontWeight: '500',
    fontSize: 12,
    width: '100%',
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 16,
    height: 16,
    borderRadius: 99,
    backgroundColor: colors.slate[200],
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
});
